import { computeTotalHoursOnBoard } from '../../../robCommonComputations'

export { computeTotalHoursOnBoard }

// ─── BDR — Bunker Delivery Receipt (supplier BDN figures) ────────────────────
//   specGravity  = density15 − 1.1
//   massMtVac    = density15 × volume15 ÷ 1000
//   weightMtAir  = specGravity × volume15 ÷ 1000

export const computeBDR = (density15Raw, volume15Raw) => {
  const d = parseFloat(density15Raw)
  const v = parseFloat(volume15Raw)
  if (isNaN(d) || isNaN(v)) return null
  const specGravity = d - 1.1

  return {
    density15: d.toFixed(3),
    massMtVac: ((d * v) / 1000).toFixed(3),
    specGravity: specGravity.toFixed(4),
    volume15: v.toFixed(3),
    weightMtAir: ((specGravity * v) / 1000).toFixed(3),
  }
}

// ─── BBM — Bunker Barge Measurements (surveyor barge figures) ─────────────────
//   density15 re-uses BDN density
//   specGravity  = density15 − 1.1
//   massMtVac    = density15 × bargeVolume15 ÷ 1000
//   weightMtAir  = specGravity × bargeVolume15 ÷ 1000

export const computeBBM = (density15Raw, bargeVolume15Raw) => {
  const d = parseFloat(density15Raw)
  const v = parseFloat(bargeVolume15Raw)
  if (isNaN(d) || isNaN(v)) return null
  const specGravity = d - 1.1

  return {
    density15: d.toFixed(3),
    massMtVac: ((d * v) / 1000).toFixed(3),
    specGravity: specGravity.toFixed(4),
    volume15: v.toFixed(3),
    weightMtAir: ((specGravity * v) / 1000).toFixed(3),
  }
}

// ─── QAS — Quantity as Supplied (lab density × barge volume) ─────────────────
//   density15    = lab test result
//   volume15     = bargeVolume → fallback to bdnVolume
//   specGravity  = labDensity − 1.1
//   massMtVac    = labDensity × volume15 ÷ 1000
//   weightMtAir  = specGravity × volume15 ÷ 1000

export const computeQAS = (labDensityRaw, bargeVolumeRaw, bdnVolumeRaw) => {
  const d = parseFloat(labDensityRaw)
  const v = parseFloat(bargeVolumeRaw) || parseFloat(bdnVolumeRaw)
  if (isNaN(d) || isNaN(v)) return null
  const specGravity = d - 1.1

  return {
    density15: d.toFixed(3),
    massMtVac: ((d * v) / 1000).toFixed(3),
    specGravity: specGravity.toFixed(4),
    volume15: v.toFixed(3),
    weightMtAir: ((specGravity * v) / 1000).toFixed(3),
  }
}

// ─── HL Vessel Tanks Measurements — Surveyor column ──────────────────────────
//   density15   = lab-tested density
//   volume15    = VSL volume (bunker.volume15CPerVsl)
//   specGravity = labDensity − 1.1
//   massMtVac   = labDensity × vslVolume ÷ 1000
//   weightMtAir = specGravity × vslVolume ÷ 1000

export const computeHLVessel = (labDensityRaw, vslVolume15Raw) => {
  const d = parseFloat(labDensityRaw)
  const v = parseFloat(vslVolume15Raw)
  if (isNaN(d) || isNaN(v)) return null
  const specGravity = d - 1.1

  return {
    density15: d.toFixed(3),
    massMtVac: ((d * v) / 1000).toFixed(3),
    specGravity: specGravity.toFixed(4),
    volume15: v.toFixed(3),
    weightMtAir: ((specGravity * v) / 1000).toFixed(3),
  }
}

// ─── Difference: Surveyor vs Supplier ────────────────────────────────────────

export const computeDiff = (surveyorVal, referenceVal) => {
  const s = parseFloat(surveyorVal)
  const r = parseFloat(referenceVal)
  if (isNaN(s) || isNaN(r)) return '—'

  return (s - r).toFixed(3)
}

export const computeDiffPct = (surveyorVal, referenceVal) => {
  const s = parseFloat(surveyorVal)
  const r = parseFloat(referenceVal)
  if (isNaN(s) || isNaN(r) || r === 0) return '—'

  return (((s - r) / r) * 100).toFixed(3)
}
// ─── Difference Pane — Surveyor vs Supplier (precision rules) ────────────────
//   nullAsZero: true  → treats missing input as 0 (volume row)
//   nullAsZero: false → returns null if either input is missing (density/mass/weight)

export const computeDiffValue = (aRaw, bRaw, { nullAsZero = false } = {}) => {
  const rawA = parseFloat(aRaw)
  const rawB = parseFloat(bRaw)
  const a = isNaN(rawA) ? (nullAsZero ? 0 : null) : rawA
  const b = isNaN(rawB) ? (nullAsZero ? 0 : null) : rawB
  if (a === null || b === null) return null

  return a - b
}

// Returns (a − b) / b × 100 rounded to 10 decimal places.
// Returns null if b is null/NaN/zero, or if a is null/NaN (unless nullAsZero).
export const computeDiffPct10dp = (aRaw, bRaw, { nullAsZero = false } = {}) => {
  const rawA = parseFloat(aRaw)
  const rawB = parseFloat(bRaw)
  const a = isNaN(rawA) ? (nullAsZero ? 0 : null) : rawA
  const b = isNaN(rawB) ? null : rawB
  if (a === null || b === null || b === 0) return null

  return parseFloat((((a - b) / b) * 100).toFixed(10))
}
// ─── Test: Aluminium + Silicon (auto-calculated) ──────────────────────────────

export const computeAlSi = (alRaw, siRaw) => {
  const al = parseFloat(alRaw)
  const si = parseFloat(siRaw)

  return !isNaN(al) && !isNaN(si) ? (al + si).toFixed(2) : '—'
}

// ─── Test: Stability Index = TSP − TSE (fallback TSA − TSE) ──────────────────

export const computeStabilityIndex = (tspRaw, tseRaw, tsaRaw) => {
  const tsp = parseFloat(tspRaw)
  const tse = parseFloat(tseRaw)
  const tsa = parseFloat(tsaRaw)

  if (!isNaN(tsp) && !isNaN(tse)) return (tsp - tse).toFixed(4)
  if (isNaN(tsp) && !isNaN(tsa) && !isNaN(tse)) return (tsa - tse).toFixed(4)

  return '—'
}
